/**
 * The person, rendered: header, actions, the tabs and the aside. The
 * SAME component fills the drawer over a list and the standalone contact
 * detail page; `mode` decides the two differences (actions vs "Open in a list").
 *
 * Owns the two reads (03 + 04). They fail independently: nu-sync being down
 * degrades the Thrive & HubSpot tab and the aside's Relationship card, and
 * nothing else.
 */
import { AlertTriangle, ExternalLink, Mail, Phone } from "lucide-react";
import { type KeyboardEvent, useId, useRef } from "react";

import type { ImportContactRow } from "@nu-assist/contracts";
import { Avatar, Spinner } from "@nu-assist/ui";

import type { AiReviewClient } from "../../clients/aiReview.client";
import { PeopleApiError, type PeopleClient } from "../../clients/people.client";
import { ActivityTab } from "./ActivityTab";
import { AiReviewTab, aiReviewRuns } from "./AiReviewTab";
import { CompanyMapTab } from "./CompanyMapTab";
import { PersonActions, type PersonListActions } from "./PersonActions";
import { PersonAside } from "./PersonAside";
import { ProfileTab } from "./ProfileTab";
import { RelationshipTab } from "./RelationshipTab";
import { ActingOnStrip, NoListSelected } from "./actionContext";
import { missingRoleCopy, personInitials, personName } from "./format";
import { AI_PROMPT_TABS, type PersonTab, isAiPromptTab } from "./personHandle";
import { resolveState } from "./personState";
import { usePerson } from "./usePerson";
import { useRelationship } from "./useRelationship";

/** The top row. "AI Prompts" is not a URL value: it holds the AI Review and
 *  Company Map tabs, which keep their own `?tab=` values. */
type TopTab = "profile" | "activity" | "sync" | "ai-prompts";
const TOP_TABS: readonly TopTab[] = ["profile", "activity", "sync", "ai-prompts"];

const TAB_LABEL: Record<TopTab | PersonTab, string> = {
  profile: "Profile",
  activity: "Activity in nu-assist",
  sync: "Thrive & HubSpot",
  "ai-prompts": "AI Prompts",
  "ai-review": "AI Review",
  "company-map": "Company Map",
};

function topTabOf(tab: PersonTab): TopTab {
  return isAiPromptTab(tab) ? "ai-prompts" : tab;
}

/** Entering a top tab: "AI Prompts" opens on AI Review. */
function personTabOf(top: TopTab): PersonTab {
  return top === "ai-prompts" ? "ai-review" : top;
}

export interface PersonViewProps {
  client: PeopleClient;
  handle: string;
  /** The list the view is over; null on the standalone page. */
  importId: string | null;
  tab: PersonTab;
  onTabChange: (tab: PersonTab) => void;
  mode: "list" | "page";
  /** The person's row in the contacts table, when it is on the current page —
   *  the pill then reads the table's own cache entry. */
  row?: ImportContactRow;
  avatarUrl?: string;
  actions?: PersonListActions;
  /** Id for the element that names the person — the drawer labels itself by it. */
  nameId?: string;
  /** The AI Review tab's client (call details, prompt viewer); defaults to the
   *  app's. */
  aiReviewClient?: AiReviewClient;
  /** The AI Review tab's prompt viewer opened (true) or closed (false). A
   *  host in a `PersonPanel` passes it to `suspended`, as for its own modals,
   *  so the panel's keys act on the viewer and not on the person behind it. */
  onModalChange?: (open: boolean) => void;
}

export function PersonView({
  client,
  handle,
  importId,
  tab,
  onTabChange,
  mode,
  row,
  avatarUrl,
  actions,
  nameId,
  aiReviewClient,
  onModalChange,
}: PersonViewProps) {
  const personQuery = usePerson(client, handle, importId);
  const relationshipQuery = useRelationship(client, handle);
  const tabsId = useId();
  const tabRefs = useRef<Record<TopTab, HTMLButtonElement | null>>({
    profile: null,
    activity: null,
    sync: null,
    "ai-prompts": null,
  });
  const promptTabRefs = useRef<Record<(typeof AI_PROMPT_TABS)[number], HTMLButtonElement | null>>({
    "ai-review": null,
    "company-map": null,
  });

  if (personQuery.isPending) {
    return (
      <div
        className="flex items-center gap-2 p-6 text-body text-text-secondary"
        data-testid="person-loading"
      >
        <Spinner /> Loading…
      </div>
    );
  }
  if (personQuery.isError || !personQuery.data) {
    const notFound =
      personQuery.error instanceof PeopleApiError && personQuery.error.status === 404;
    return (
      <div className="p-6" data-testid="person-error">
        <p className="text-body text-text-secondary">
          {notFound
            ? "No list in nu-assist has this person."
            : "Could not load this person. Try again in a moment."}
        </p>
        {!notFound && (
          <button
            type="button"
            onClick={() => void personQuery.refetch()}
            className="mt-2 text-caption text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  const person = personQuery.data;
  const identity = person.identity;
  const name = personName(person);
  const resolved = resolveState(person, row);
  const relationship = relationshipQuery.data;
  const people = new Set((person.appearances ?? []).map((a) => a.uploaded_by));
  const listCount = (person.appearances ?? []).length;
  // One run per review id: a run's parent list and its AI Review file can both
  // be appearances of this person, each listing the same run.
  const aiReviewRunCount = aiReviewRuns(person).length;
  const syncContact = relationship?.contacts[0];
  const email = identity.email || identity.work_email;
  // In a list the acting row answers (it is what Export sends). Off-page row
  // (a `?person=` deep link): no row to read, so the person answers — a
  // person with no role anywhere has none in this list either. The page never
  // passes a row; the branch keeps a stray row out of page mode.
  const roleSource = mode === "list" ? (row ?? identity) : identity;
  const missingRole = missingRoleCopy(roleSource, mode);

  function onTabKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    e.stopPropagation();
    const i = TOP_TABS.indexOf(topTabOf(tab));
    const next =
      TOP_TABS[(i + (e.key === "ArrowRight" ? 1 : TOP_TABS.length - 1)) % TOP_TABS.length];
    if (!next) return;
    onTabChange(personTabOf(next));
    tabRefs.current[next]?.focus();
  }

  function onPromptTabKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    e.stopPropagation();
    const i = AI_PROMPT_TABS.indexOf(tab as (typeof AI_PROMPT_TABS)[number]);
    const next =
      AI_PROMPT_TABS[
        (i + (e.key === "ArrowRight" ? 1 : AI_PROMPT_TABS.length - 1)) % AI_PROMPT_TABS.length
      ];
    if (!next) return;
    onTabChange(next);
    promptTabRefs.current[next]?.focus();
  }

  // "AI Prompts" carries no count of its own; its two tabs do.
  const counts: Record<TopTab | PersonTab, number | null> = {
    profile: null,
    activity: (person.events ?? []).length,
    sync: relationship ? relationship.items.length : null,
    "ai-prompts": null,
    "ai-review": aiReviewRunCount,
    "company-map": (person.company_map ?? []).length,
  };
  const topTab = topTabOf(tab);

  return (
    <div className="flex flex-col gap-4 p-4 xl:flex-row xl:items-start" data-testid="person-view">
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {mode === "list" && <ActingOnStrip actingOn={actions?.actingOn} />}
        <section className="rounded-lg border border-border bg-surface">
          <div className="flex flex-col gap-4 p-4 sm:flex-row">
            <Avatar
              src={avatarUrl ?? null}
              name={name}
              initials={personInitials(person)}
              size="2xl"
            />
            <div className="min-w-0 flex-1">
              <h2 id={nameId} className="flex flex-wrap items-baseline gap-x-2 text-h1 text-text">
                {name}
                {identity.connection_degree && (
                  <span className="text-body font-normal text-text-muted">
                    · {identity.connection_degree}
                  </span>
                )}
              </h2>
              {identity.headline && (
                <p className="text-body text-text-secondary">{identity.headline}</p>
              )}
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-caption text-text-secondary">
                {/* Firm-wide on purpose: `identity` carries the newest role across
                    every list, while the "No position…" caption below reads THIS
                    list's row. Both can show at once — the person has a job in
                    another list and none in this one. Follow-up in the PRD: read
                    the row here too, or suffix "(from another list)". */}
                {(identity.current_position || identity.current_company) && (
                  <span>
                    {[identity.current_position, identity.current_company]
                      .filter(Boolean)
                      .join(" at ")}
                  </span>
                )}
                {identity.location && <span>{identity.location}</span>}
                <span>
                  In {listCount} list{listCount === 1 ? "" : "s"}
                  {people.size > 1 && ` by ${people.size} people`}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-caption text-text-secondary">
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {email ? (
                    <span className="text-text">{email}</span>
                  ) : syncContact?.has_personal_email || syncContact?.has_work_email ? (
                    "Email on file in Thrive"
                  ) : (
                    "No email yet. Looked up on Add to search."
                  )}
                </span>
                {syncContact?.has_phone && (
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="h-4 w-4" aria-hidden="true" />
                    Phone on file in Thrive
                  </span>
                )}
                {missingRole && (
                  <span
                    className="inline-flex items-center gap-1.5 text-warning-ink"
                    data-testid="person-missing-role"
                  >
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {missingRole}
                  </span>
                )}
                <a
                  href={person.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-accent-ink hover:underline"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  LinkedIn profile
                </a>
              </div>
              <PersonActions resolved={resolved} actions={mode === "list" ? actions : undefined} />
              {mode === "page" && <NoListSelected person={person} tab={tab} />}
            </div>
          </div>
          <div
            role="tablist"
            aria-label="Person details"
            className="flex gap-1 overflow-x-auto border-t border-border px-4"
            onKeyDown={onTabKeyDown}
          >
            {TOP_TABS.map((t) => (
              <button
                key={t}
                ref={(el) => {
                  tabRefs.current[t] = el;
                }}
                type="button"
                role="tab"
                id={`${tabsId}-tab-${t}`}
                aria-selected={topTab === t}
                aria-controls={`${tabsId}-panel`}
                tabIndex={topTab === t ? 0 : -1}
                data-testid={`person-tab-${t}`}
                onClick={() => {
                  if (topTab !== t) onTabChange(personTabOf(t));
                }}
                className={`whitespace-nowrap border-b-2 px-2 py-3 text-label focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                  topTab === t
                    ? "border-accent text-accent-ink"
                    : "border-transparent text-text-secondary hover:text-text"
                }`}
              >
                {TAB_LABEL[t]}
                {counts[t] != null && (
                  <span className="ml-1 font-normal tabular-nums text-text-muted">{counts[t]}</span>
                )}
              </button>
            ))}
          </div>
        </section>

        <div role="tabpanel" id={`${tabsId}-panel`} aria-labelledby={`${tabsId}-tab-${topTab}`}>
          {tab === "profile" && (
            <ProfileTab
              person={person}
              client={client}
              onSeeModelCalls={() => onTabChange("ai-review")}
              onSeeCompanyMapCalls={() => onTabChange("company-map")}
            />
          )}
          {tab === "activity" && <ActivityTab person={person} />}
          {isAiPromptTab(tab) && (
            <>
              <div
                role="tablist"
                aria-label="AI Prompts"
                className="mb-4 flex gap-1 border-b border-border"
                onKeyDown={onPromptTabKeyDown}
              >
                {AI_PROMPT_TABS.map((t) => (
                  <button
                    key={t}
                    ref={(el) => {
                      promptTabRefs.current[t] = el;
                    }}
                    type="button"
                    role="tab"
                    id={`${tabsId}-prompt-tab-${t}`}
                    aria-selected={tab === t}
                    aria-controls={`${tabsId}-prompt-panel`}
                    tabIndex={tab === t ? 0 : -1}
                    data-testid={`person-tab-${t}`}
                    onClick={() => onTabChange(t)}
                    className={`whitespace-nowrap border-b-2 px-2 py-2 text-label focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                      tab === t
                        ? "border-accent text-accent-ink"
                        : "border-transparent text-text-secondary hover:text-text"
                    }`}
                  >
                    {TAB_LABEL[t]}
                    <span className="ml-1 font-normal tabular-nums text-text-muted">
                      {counts[t]}
                    </span>
                  </button>
                ))}
              </div>
              <div
                role="tabpanel"
                id={`${tabsId}-prompt-panel`}
                aria-labelledby={`${tabsId}-prompt-tab-${tab}`}
              >
                {tab === "ai-review" && (
                  <AiReviewTab
                    person={person}
                    client={aiReviewClient}
                    onModalChange={onModalChange}
                  />
                )}
                {tab === "company-map" && (
                  <CompanyMapTab person={person} onModalChange={onModalChange} />
                )}
              </div>
            </>
          )}
          {tab === "sync" && (
            <RelationshipTab
              person={person}
              data={relationship}
              error={relationshipQuery.error}
              isLoading={relationshipQuery.isPending}
              onRetry={() => void relationshipQuery.refetch()}
            />
          )}
        </div>
      </div>

      <div className="shrink-0 xl:sticky xl:top-0 xl:w-72">
        <PersonAside
          person={person}
          relationship={relationship}
          relationshipError={relationshipQuery.error}
          relationshipLoading={relationshipQuery.isPending}
          mode={mode}
          tab={tab}
          currentImportId={mode === "list" ? importId : null}
        />
      </div>
    </div>
  );
}
